import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Button, H3, SidebarButton, SidebarHeaderTitle, SidebarTitle } from 'components';
import { theme, toRem } from 'design';
import { SidebarMobileActions } from 'screens/MakePayment/components';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';
import SummaryAccordion, { ISummaryAccordionData } from '../SummaryAccordion/SummaryAccordion';

interface ISegmentTipTotals {
  name: string;
  data: ISummaryAccordionData;
}

export interface ITipTotals {
  cashTips?: number;
  cashAutograts?: number;
  ccTips?: number;
  ccAutograts?: number;
  ccFeeSum?: number;
  splitChecksTotal?: number;
  ignoredChecksTotal?: number;
}

export interface ISummaryProps {
  allowedToProceed?: boolean;
  className?: string;
  isLoading?: boolean;
  segments?: ISegmentTipTotals[];
  totals: ITipTotals;
  onExpand?: () => void;
  onBackClick?: () => void;
  onNextClick?: () => void;
  rightActionLabel?: string;
  sidebarOpen?: boolean;
  creditCardPercentFee?: number;
  creditCardFeeEnabled?: boolean;
  formattedDate: string;
}

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${toRem(40)};
  & > * {
    flex-grow: 1;
    &:not(:last-child) {
      margin-right: ${toRem(16)};
    }
  }
`;

const HeaderText = styled.p`
  font-size: ${toRem(14)};
  font-weight: 500;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${toRem(8)};

  span {
    font-size: ${toRem(14)};
    color: ${theme.color.neutral[500]};
  }
`;

const SegmentSummaryAccordion = styled(SummaryAccordion)`
  &:not(:first-child) {
    margin-top: ${toRem(24)};
  }
`;

const TipAmountEntrySegmentsSummaryLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  padding: ${toRem(16)} ${toRem(40)};
  background: ${theme.color.neutral[50]};
  border-left: 1px solid ${theme.color.neutral[300]};

  @media (min-width: ${theme.breakpoint.large}px) {
    padding: ${toRem(40)};
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: ${toRem(24)};

  @media (min-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`;

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${theme.breakpoint.large}px) {
    flex-grow: 1;
  }
`;

const TipAmountSummarySection = styled.div`
  display: flex;
  flex-direction: column;
`;

const TipTotalHeader = styled(Row)`
  display: flex;
  flex-direction: column;
  margin-bottom: ${toRem(24)};

  h3 {
    margin-bottom: ${toRem(8)};
  }
`;

const NetFooter = styled(Row)`
  margin-top: ${toRem(24)};
`;

const Summary: React.FC<ISummaryProps> = ({
  allowedToProceed,
  className = '',
  isLoading,
  segments,
  totals,
  onBackClick,
  onExpand,
  onNextClick,
  rightActionLabel = 'Next',
  sidebarOpen,
  creditCardPercentFee = 0,
  creditCardFeeEnabled = false,
  formattedDate,
}) => {
  const segmentsSection = useMemo(() => {
    if (!segments?.length) return null;

    const accordions = segments.map((segment) => <SegmentSummaryAccordion key={segment.name} data={segment.data} title={segment.name} isLoading={isLoading} defaultOpenState />);

    return <TipAmountSummarySection>{accordions}</TipAmountSummarySection>;
  }, [segments, isLoading]);

  return (
    <TipAmountEntrySegmentsSummaryLayout data-cy="tip-data-summary" className={className}>
      <MobileHeader>
        <SidebarHeaderTitle>
          <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
          <SidebarTitle title="Tip Amount Summary" />
        </SidebarHeaderTitle>
        <SidebarMobileActions columns={2}>
          <Button data-cy="tip-data-back-button" label="Back" type="button" appearance="white" size="small" onClick={onBackClick} />

          <Button data-cy="tip-data-next-button" label={rightActionLabel} type="button" appearance="primary" disabled={!allowedToProceed} size="small" onClick={onNextClick} />
        </SidebarMobileActions>
      </MobileHeader>
      <SummaryWrapper>
        <TipAmountSummarySection>
          <TipTotalHeader data-cy="tip-total">
            <H3>{formattedDate}</H3>
            <Row>
              <HeaderText>Collected Tips & Autograts</HeaderText>
              {isLoading ? (
                <Skeleton width={80} />
              ) : (
                <HeaderText>{centsToDollarsDisplay((totals.cashTips || 0) + (totals.cashAutograts || 0) + (totals.ccTips || 0) + (totals.ccAutograts || 0) + (totals.splitChecksTotal || 0) + (totals.ignoredChecksTotal || 0))}</HeaderText>
              )}
            </Row>
          </TipTotalHeader>
          <Row>
            <HeaderText>Tip Details</HeaderText>
          </Row>
          <Row data-cy="cc-tips-summary">
            <span>Credit Card Tips</span>
            {isLoading ? <Skeleton width={50} /> : <span>{centsToDollarsDisplay(totals.ccTips || 0)}</span>}
          </Row>
          <Row data-cy="cc-autograts-summary">
            <span>Credit Card Autograts</span>
            {isLoading ? <Skeleton width={50} /> : <span>{centsToDollarsDisplay(totals.ccAutograts || 0)}</span>}
          </Row>
          <Row data-cy="cash-tips-summary">
            <span>Cash Tips</span>
            {isLoading ? <Skeleton width={50} /> : <span>{centsToDollarsDisplay(totals.cashTips || 0)}</span>}
          </Row>
          <Row data-cy="cash-autograts-summary">
            <span>Cash Autograts</span>
            {isLoading ? <Skeleton width={50} /> : <span>{centsToDollarsDisplay(totals.cashAutograts || 0)}</span>}
          </Row>
          {creditCardFeeEnabled && (
            <Row data-cy="cc-fee-summary">
              <span>Credit Fee Deduction ({creditCardPercentFee}%)</span>
              {isLoading ? <Skeleton width={50} /> : <span>{centsToNegativeDollarsDisplay(totals.ccFeeSum)}</span>}
            </Row>
          )}
          {totals.splitChecksTotal > 0 && (
            <Row data-cy="split-checks-summary">
              <span>Split Checks</span>
              {isLoading ? <Skeleton width={50} /> : <span>{centsToDollarsDisplay(totals.splitChecksTotal || 0)}</span>}
            </Row>
          )}
          {totals.ignoredChecksTotal > 0 && (
            <Row data-cy="ignored-checks-summary">
              <span>Ignored Checks</span>
              {isLoading ? <Skeleton width={50} /> : <span>{centsToNegativeDollarsDisplay(totals.ignoredChecksTotal || 0)}</span>}
            </Row>
          )}
          <NetFooter>
            <HeaderText>Net after deductions</HeaderText>
            {isLoading ? (
              <Skeleton width={80} />
            ) : (
              <HeaderText>
                {centsToDollarsDisplay((totals.cashTips || 0) + (totals.cashAutograts || 0) + (totals.ccTips || 0) + (totals.ccAutograts || 0) + (totals.splitChecksTotal || 0) - (totals.ignoredChecksTotal || 0) - totals.ccFeeSum)}
              </HeaderText>
            )}
          </NetFooter>
        </TipAmountSummarySection>
        {segmentsSection}
      </SummaryWrapper>
      <ButtonsWrapper>
        <Button data-cy="tip-data-back-button" label="Back" type="button" appearance="white" size="large" onClick={onBackClick} />

        <Button data-cy="tip-data-next-button" label={rightActionLabel} type="button" appearance="primary" disabled={!allowedToProceed} size="large" onClick={onNextClick} />
      </ButtonsWrapper>
    </TipAmountEntrySegmentsSummaryLayout>
  );
};

export default Summary;
